import { useState, useEffect, useCallback } from 'react';
import { PracticeProvider, usePractice } from './context/PracticeContext';
import { useKeyboardInput } from './hooks/useKeyboardInput';
import { useAudio } from './hooks/useAudio';
import { Flashcard } from './components/Flashcard';
import { PianoKeyboard } from './components/PianoKeyboard';
import { StatsBar } from './components/StatsBar';
import { SettingsPanel, SettingsButton } from './components/SettingsPanel';
import { Button } from './components/ui/Button';

/**
 * Main practice area component
 */
function PracticeArea() {
  const {
    state,
    startSession,
    handleNoteInput,
    handleClickReveal,
    nextCard
  } = usePractice();

  const { currentCard, isStarted, settings, sessionStats, pianoHighlight, feedbackMessage, progressionStages, activeDeck } = state;

  // Synthesized audio hook (FL Studio WAV files need ffmpeg conversion)
  // TODO: Switch to useNoteAudio once audio files are converted to standard PCM
  const { playNote, resumeAudio } = useAudio({
    enabled: settings.playAudio,
    volume: 0.3
  });

  // Keyboard input hook
  useKeyboardInput({
    enabled: isStarted && settings.inputMode !== 'click' && !currentCard?.isRevealed,
    onNoteInput: handleNoteInput
  });

  // Play audio when card is revealed
  useEffect(() => {
    if (currentCard?.isRevealed && settings.playAudio && currentCard.note) {
      playNote(currentCard.note.midiNumber);
    }
  }, [currentCard?.isRevealed, currentCard?.note, settings.playAudio, playNote]);

  // Handle start button click
  const handleStart = useCallback(async () => {
    await resumeAudio();
    startSession();
  }, [resumeAudio, startSession]);

  // Handle next button click
  const handleNext = useCallback(() => {
    if (currentCard?.isRevealed) {
      nextCard();
    }
  }, [currentCard?.isRevealed, nextCard]);

  return (
    <div className="practice-area flex flex-col items-center justify-center flex-1 px-4 py-8">
      {!isStarted ? (
        // Start screen
        <div className="text-center">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Ready to Practice?
            </h2>
            <p className="text-gray-600 mb-4">
              {settings.clefSelection === 'treble' && 'Treble Clef Mode'}
              {settings.clefSelection === 'bass' && 'Bass Clef Mode'}
              {settings.clefSelection === 'mixed' && 'Mixed Clefs Mode'}
            </p>
            <p className="text-sm text-gray-500">
              {activeDeck.length} notes in deck
            </p>
          </div>
          <Button size="lg" onClick={handleStart}>
            Start Practice
          </Button>
          <p className="text-sm text-gray-500 mt-4">
            {settings.inputMode === 'click' && 'Click on cards to reveal answers'}
            {settings.inputMode === 'keyboard' && 'Use keyboard (A-G) to identify notes'}
            {settings.inputMode === 'mixed' && 'Type note name or click to reveal'}
          </p>
        </div>
      ) : (
        // Practice mode
        <div className="w-full max-w-lg">
          {/* Flashcard */}
          <div className="mb-8 relative">
            <Flashcard
              note={currentCard?.note || null}
              isRevealed={currentCard?.isRevealed || false}
              wasCorrect={currentCard?.wasCorrect || null}
              onClick={handleClickReveal}
              colorCode={settings.colorCodeNotes}
              feedbackMessage={feedbackMessage}
            />
          </div>

          {/* Next button (shown when revealed) */}
          {currentCard?.isRevealed && (
            <div className="flex justify-center mb-8">
              <Button onClick={handleNext} variant="primary">
                Next Card
              </Button>
            </div>
          )}

          {/* Keyboard hint */}
          {!currentCard?.isRevealed && settings.inputMode !== 'click' && (
            <div className="text-center text-sm text-gray-500 mb-4">
              <p>Type the note name (A-G)</p>
              <p className="text-xs mt-1">
                Left Shift + key = flat, Right Shift + key = sharp
              </p>
            </div>
          )}
        </div>
      )}

      {/* Piano Keyboard */}
      {settings.showPiano && isStarted && (
        <div className="mt-8">
          <PianoKeyboard
            highlightedPitchClass={pianoHighlight.pitchClass}
            highlightedAccidental={pianoHighlight.accidental}
            highlightType={pianoHighlight.type}
            interactive={false}
          />
        </div>
      )}

      {/* Stats (when practicing) */}
      {isStarted && (
        <div className="mt-8 w-full max-w-2xl">
          <StatsBar
            stats={sessionStats}
            clefSelection={settings.clefSelection}
            progressionStages={progressionStages}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Header component
 */
function Header({ onOpenSettings }: { onOpenSettings: () => void }) {
  return (
    <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-30">
      <div className="max-w-4xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">
              Music Note Flashcards
            </h1>
            <p className="text-xs text-gray-500 hidden sm:block">
              Learn to read treble and bass clef
            </p>
          </div>
        </div>
        <SettingsButton onClick={onOpenSettings} />
      </div>
    </header>
  );
}

/**
 * Footer component
 */
function Footer() {
  return (
    <footer className="bg-gray-50 border-t border-gray-200 px-4 py-4 text-center text-sm text-gray-500">
      <p>
        Practice makes perfect! Keep drilling to improve your note reading skills.
      </p>
    </footer>
  );
}

/**
 * Main App component with provider
 */
function AppContent() {
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header onOpenSettings={() => setSettingsOpen(true)} />

      <main className="flex-1 flex flex-col">
        <PracticeArea />
      </main>

      <Footer />

      <SettingsPanel
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </div>
  );
}

/**
 * Root App component wrapped with PracticeProvider
 */
export default function App() {
  return (
    <PracticeProvider>
      <AppContent />
    </PracticeProvider>
  );
}
